<template>
  <!-- Sidebar à direita, no padrão dos demais formulários do Oráculo (ex.: NewLeadSidebar).
       Largura ~30rem: todos os campos ocupam a coluna inteira — os breakpoints do b-col
       olham a janela, não o sidebar, então md="6" espremeria os selects no desktop. -->
  <b-sidebar
    id="sidebar-direct-conversation"
    sidebar-class="sidebar-lg"
    :visible="visible"
    bg-variant="white"
    shadow
    backdrop
    right
    no-header
    :no-close-on-backdrop="submitting"
    :no-close-on-esc="submitting"
    aria-label="Direcionar conversa"
    @hidden="onHidden"
    @change="$emit('update:visible', $event)"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h4 class="mb-0">
          Direcionar conversa
        </h4>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          role="button"
          aria-label="Fechar"
          @click="!submitting && hide()"
        />
      </div>

      <div class="p-2">
        <p class="text-muted mb-2">
          Convide outro planejador ou alguém de outra unidade para responder
          <strong>{{ customerName }}</strong> junto com você. O cliente vê quem entrou na conversa.
        </p>

        <!-- Seção 1: para quem direcionar -->
        <h6 class="text-uppercase text-muted font-small-3 font-weight-bolder mb-1">
          Para quem direcionar
        </h6>

        <b-row>
          <b-col cols="12">
            <b-form-group
              label="Tipo de pessoa"
              label-for="direct-type"
              class="mb-1"
            >
              <b-form-select
                id="direct-type"
                v-model="type"
                :options="typeOptions"
                @change="search"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="departments.length"
            cols="12"
          >
            <b-form-group
              label="Unidade"
              label-for="direct-department"
              class="mb-1"
            >
              <b-form-select
                id="direct-department"
                v-model="departmentId"
                :options="departmentOptions"
                @change="search"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          label="Pessoa"
          label-for="direct-keyword"
          class="mb-1"
        >
          <b-input-group class="input-group-merge">
            <b-input-group-prepend is-text>
              <feather-icon
                icon="SearchIcon"
                aria-hidden="true"
              />
            </b-input-group-prepend>
            <b-form-input
              id="direct-keyword"
              v-model="keyword"
              placeholder="Digite ao menos 2 letras"
              autocomplete="off"
              @input="scheduleSearch"
            />
          </b-input-group>
        </b-form-group>

        <small
          v-if="!loading && !searchError && results.length"
          class="text-muted d-block mb-50"
        >
          {{ results.length }} {{ results.length === 1 ? 'pessoa encontrada' : 'pessoas encontradas' }}
        </small>

        <div
          class="customer-chat-invitable mb-0"
          aria-live="polite"
        >
          <div
            v-if="loading"
            class="text-center py-2"
          >
            <b-spinner
              small
              variant="primary"
            />
            <span class="ml-50 text-muted">Buscando pessoas…</span>
          </div>
          <p
            v-else-if="searchError"
            class="text-danger text-center py-2 mb-0"
          >
            <feather-icon
              icon="AlertCircleIcon"
              class="mr-50"
              aria-hidden="true"
            />
            Não foi possível buscar as pessoas. Tente novamente.
          </p>
          <p
            v-else-if="!results.length"
            class="text-muted text-center py-2 mb-0"
          >
            Ninguém encontrado com esses filtros. Tente outro nome ou unidade.
          </p>
          <b-list-group
            v-else
            flush
          >
            <b-list-group-item
              v-for="user in results"
              :key="user.coreUserId"
              button
              :active="isSelected(user)"
              :aria-pressed="isSelected(user) ? 'true' : 'false'"
              class="d-flex align-items-center justify-content-between py-1 px-1"
              @click="selected = user"
            >
              <span class="text-truncate">
                <span class="font-weight-bolder">{{ user.name }}</span>
                <small
                  v-if="user.unitLabel"
                  class="d-block mt-25"
                  :class="isSelected(user) ? 'text-white' : 'text-muted'"
                >{{ user.unitLabel }}</small>
              </span>
              <span class="d-flex align-items-center flex-shrink-0">
                <b-badge
                  v-if="user.suggested"
                  pill
                  variant="light-success"
                  title="Já atende este cliente pelo Atendimento Prioritário ou por compartilhamento"
                  aria-label="Já atende este cliente pelo Atendimento Prioritário ou por compartilhamento"
                >
                  Sugerido
                </b-badge>
                <feather-icon
                  v-if="isSelected(user)"
                  icon="CheckIcon"
                  size="16"
                  class="ml-1 flex-shrink-0"
                  aria-hidden="true"
                />
              </span>
            </b-list-group-item>
          </b-list-group>
        </div>

        <!-- Seção 2: configurar convite -->
        <template v-if="selected">
          <hr class="my-2">

          <h6 class="text-uppercase text-muted font-small-3 font-weight-bolder mb-1">
            Configurar convite
          </h6>

          <div class="bg-light-primary rounded p-1 mb-2 d-flex align-items-center">
            <feather-icon
              icon="UserCheckIcon"
              class="mr-1 text-primary flex-shrink-0"
              aria-hidden="true"
            />
            <span class="text-truncate">
              <strong>{{ selected.name }}</strong>
              <small
                v-if="selected.unitLabel"
                class="text-muted d-block"
              >{{ selected.unitLabel }}</small>
            </span>
          </div>

          <b-form-group
            label="O que a pessoa poderá ver"
            label-class="font-weight-bolder"
            class="mb-2"
          >
            <b-form-radio-group
              v-model="historyScope"
              :options="historyOptions"
              stacked
              class="mt-50"
            />
            <b-alert
              v-if="historyScope === historyFull"
              variant="warning"
              show
              class="mt-1 mb-0"
            >
              <div class="alert-body py-50">
                <small>Todo o histórico com o cliente ficará visível para essa pessoa. Compartilhe só se for necessário; a ação fica registrada.</small>
              </div>
            </b-alert>
          </b-form-group>

          <b-form-group
            label="Nota interna (opcional)"
            label-for="direct-note"
            description="Visível só para a equipe, nunca para o cliente."
            class="mb-2"
          >
            <b-form-textarea
              id="direct-note"
              v-model="note"
              rows="3"
              :maxlength="noteMaxLength"
              placeholder="Ex.: dúvida sobre a carteira de investimentos"
            />
            <small class="text-muted d-block text-right mt-25">{{ note.length }}/{{ noteMaxLength }}</small>
          </b-form-group>

          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Prazo do acesso"
                label-for="direct-expiry"
                description="Depois disso a pessoa deixa de ver a conversa."
                class="mb-0"
              >
                <b-form-select
                  id="direct-expiry"
                  v-model="expiryDays"
                  :options="expiryOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </template>

        <b-alert
          v-if="submitError"
          variant="danger"
          show
          class="mt-2 mb-0"
          role="alert"
        >
          <div class="alert-body">
            {{ submitError }}
          </div>
        </b-alert>
      </div>
    </template>

    <template #footer="{ hide }">
      <div class="d-flex justify-content-end align-items-center border-top px-2 py-1">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="submitting"
          @click="hide"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="!selected || submitting"
          @click="submit"
        >
          <b-spinner
            v-if="submitting"
            small
            class="mr-50"
          />
          {{ submitting ? 'Direcionando…' : 'Direcionar' }}
        </b-button>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BButton, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BFormRadioGroup, BFormTextarea,
  BListGroup, BListGroupItem, BBadge, BSpinner, BAlert, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'
import * as types from '../store/types'
import {
  EXPIRY_OPTIONS,
  TYPE_OPTIONS,
  HISTORY_FROM_NOW,
  HISTORY_FULL,
  NOTE_MAX_LENGTH,
  buildInvitePayload,
  filterInvitable,
  inviteErrorMessage,
} from '../utils/participants'

const SEARCH_DEBOUNCE_MS = 300

export default {
  components: {
    BSidebar,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormRadioGroup,
    BFormTextarea,
    BListGroup,
    BListGroupItem,
    BBadge,
    BSpinner,
    BAlert,
    BInputGroup,
    BInputGroupPrepend,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    conversation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      keyword: '',
      type: null,
      departmentId: null,
      departments: [],
      results: [],
      loading: false,
      searchError: false,
      selected: null,
      historyScope: HISTORY_FROM_NOW,
      note: '',
      expiryDays: null,
      submitting: false,
      submitError: '',
      searchTimer: null,
      searchSeq: 0,
      typeOptions: TYPE_OPTIONS,
      expiryOptions: EXPIRY_OPTIONS,
      historyFull: HISTORY_FULL,
      noteMaxLength: NOTE_MAX_LENGTH,
      historyOptions: [
        { value: HISTORY_FROM_NOW, text: 'Só a partir de agora (recomendado)' },
        { value: HISTORY_FULL, text: 'Todo o histórico da conversa' },
      ],
    }
  },
  computed: {
    customerName() {
      return this.conversation.customerDisplayName || 'o cliente'
    },
    departmentOptions() {
      return [{ value: null, text: 'Todas as unidades' }].concat(
        this.departments.map(department => ({ value: department.id, text: department.name })),
      )
    },
  },
  watch: {
    visible(value) {
      if (value) this.open()
    },
  },
  mounted() {
    if (this.visible) this.open()
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    isSelected(user) {
      return !!this.selected && this.selected.coreUserId === user.coreUserId
    },
    open() {
      this.search()
      if (!this.departments.length) {
        this.$store.dispatch(types.FETCH_DEPARTMENTS)
          .then(list => { this.departments = list })
          // Sem permissão para listar departamentos: o filtro simplesmente não aparece.
          .catch(() => { this.departments = [] })
      }
    },
    scheduleSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.search, SEARCH_DEBOUNCE_MS)
    },
    search() {
      this.searchSeq += 1
      const seq = this.searchSeq
      this.loading = true
      this.searchError = false
      this.$store.dispatch(types.FETCH_INVITABLE_USERS, {
        keyword: this.keyword,
        type: this.type,
        departmentId: this.departmentId,
        customerId: this.conversation.coreCustomerId,
      })
        .then(users => {
          if (seq !== this.searchSeq) return
          this.results = filterInvitable(users, this.conversation)
          if (this.selected && !this.results.some(user => user.coreUserId === this.selected.coreUserId)) {
            this.selected = null
          }
        })
        .catch(() => {
          if (seq === this.searchSeq) this.searchError = true
        })
        .finally(() => {
          if (seq === this.searchSeq) this.loading = false
        })
    },
    submit() {
      if (!this.selected || this.submitting) return
      this.submitting = true
      this.submitError = ''
      const payload = buildInvitePayload({
        coreUserId: this.selected.coreUserId,
        historyScope: this.historyScope,
        note: this.note,
        expiryDays: this.expiryDays,
      })
      this.$store.dispatch(types.INVITE_PARTICIPANT, {
        conversationId: this.conversation.conversationId,
        payload,
      })
        .then(participant => {
          this.$emit('invited', participant)
          this.$emit('update:visible', false)
        })
        .catch(error => {
          this.submitError = inviteErrorMessage(error)
        })
        .finally(() => {
          this.submitting = false
        })
    },
    onHidden() {
      Object.assign(this, {
        keyword: '',
        type: null,
        departmentId: null,
        results: [],
        selected: null,
        historyScope: HISTORY_FROM_NOW,
        note: '',
        expiryDays: null,
        submitError: '',
        searchError: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.customer-chat-invitable {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid rgba(34, 41, 47, 0.125);
  border-radius: 0.357rem;
}

// Num flex, o text-truncate só corta com min-width: 0 — sem isso, um nome longo empurra o
// badge "Sugerido" para fora da largura estreita do sidebar.
.text-truncate {
  min-width: 0;
}
</style>
